import { trpc } from "@/utils/trpc";
import { AnimatePresence, motion } from "framer-motion";
import { useState, useEffect } from "react";

import { useConnectDispatch, useConnectState } from "../ConnectContext";
import AccountSelectItem from "../components/AccountSelectItem";
import ConnectStep from "../components/ConnectStep";
import { AccountCardSkeleton } from "../components/ShimmerSkeleton";
import { ActionType, AccountSelectionMode } from "../types";

const AccountSelectStep = () => {
  const {
    institution,
    connectSessionId,
    externalUserId,
    appConfig,
    accountSelectionMode: stateSelectionMode,
  } = useConnectState();
  const dispatch = useConnectDispatch();
  const showBalances = appConfig?.permissions.balances.enabled ?? true;
  const accountsQuery = trpc.connect.accounts.useQuery({
    connectSessionId: connectSessionId!,
  });
  const selectAccountsMutation = trpc.connect.grantAccess.useMutation();

  // Default to "multi" if not specified
  const selectionMode: AccountSelectionMode = stateSelectionMode || "multi";
  const isAllMode = selectionMode === "all";
  const isSingleMode = selectionMode === "single";

  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);

  // For "all" mode, auto-select all accounts when they load
  useEffect(() => {
    if (isAllMode && accountsQuery.data?.accounts) {
      const allAccountIds = accountsQuery.data.accounts.map(
        (account) => account.id,
      );
      setSelectedAccountIds(allAccountIds);
    }
  }, [isAllMode, accountsQuery.data?.accounts]);

  const onClickAccount = (id: string) => {
    if (isAllMode) {
      // In "all" mode, clicking doesn't change selection
      return;
    }

    if (isSingleMode) {
      // In "single" mode, only one account can be selected at a time
      if (selectedAccountIds.includes(id)) {
        setSelectedAccountIds([]);
      } else {
        setSelectedAccountIds([id]);
      }
    } else {
      // "multi" mode - toggle selection
      if (selectedAccountIds.includes(id)) {
        setSelectedAccountIds(
          selectedAccountIds.filter((selId) => selId !== id),
        );
      } else {
        setSelectedAccountIds([...selectedAccountIds, id]);
      }
    }
  };

  const handleClickNext = async () => {
    if (!accountsQuery.data) return;

    const { success, connectionId, accounts, user, scopedToken } =
      await selectAccountsMutation.mutateAsync({
        connectSessionId: connectSessionId!,
        accountIds: selectedAccountIds,
        externalUserId: externalUserId,
      });

    if (success) {
      if (!accounts?.accounts) {
        console.error(
          "Unexpected missing accounts after successful connection",
        );
        return;
      }
      dispatch({
        type: ActionType.SetSelectedAccounts,
        payload: {
          connectionId,
          accounts: accounts.accounts,
          user,
          scopedToken,
        },
      });
    }
  };

  // Different title and description based on selection mode
  const getTitle = () => {
    if (isAllMode) {
      return "Tus cuentas";
    }
    if (isSingleMode) {
      return "Selecciona tu cuenta";
    }
    return "Selecciona tus cuentas";
  };

  const getDescription = () => {
    const appName = appConfig?.name || "la aplicación";
    if (isAllMode) {
      return (
        <>
          Las siguientes cuentas de{" "}
          <span className="font-medium text-foreground">
            {institution?.name}
          </span>{" "}
          serán conectadas a{" "}
          <span className="font-medium text-foreground">{appName}</span>.
        </>
      );
    }
    if (isSingleMode) {
      return (
        <>
          Selecciona la cuenta de{" "}
          <span className="font-medium text-foreground">
            {institution?.name}
          </span>{" "}
          que deseas conectar a{" "}
          <span className="font-medium text-foreground">{appName}</span>.
        </>
      );
    }
    return (
      <>
        Selecciona las cuentas de{" "}
        <span className="font-medium text-foreground">{institution?.name}</span>{" "}
        que deseas conectar a{" "}
        <span className="font-medium text-foreground">{appName}</span>.
      </>
    );
  };

  const getButtonLabel = () => {
    if (isAllMode) {
      return "Continuar";
    }
    if (selectedAccountIds.length === 0) {
      return "Seleccionar";
    }
    // Single mode: always show singular "Conectar cuenta"
    if (isSingleMode) {
      return "Conectar cuenta";
    }
    // Multi mode: show count
    if (selectedAccountIds.length === 1) {
      return "Conectar 1 cuenta";
    }
    return `Conectar ${selectedAccountIds.length} cuentas`;
  };

  return (
    <ConnectStep
      stepId="accounts"
      title={getTitle()}
      description={getDescription()}
      nextLoading={selectAccountsMutation.isLoading}
      nextButtonLabel={getButtonLabel()}
      nextButtonDisabled={selectedAccountIds.length === 0}
      onClickNext={handleClickNext}
    >
      <AnimatePresence mode="wait">
        {accountsQuery.isLoading ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-2 mt-1"
          >
            {[0, 1, 2].map((i) => (
              <AccountCardSkeleton key={i} index={i} />
            ))}
          </motion.div>
        ) : (
          <motion.div
            key="loaded"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-2 mt-1"
          >
            {accountsQuery.data?.accounts.map((account, index) => (
              <motion.div
                key={account.id}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.25,
                  delay: index * 0.05,
                  ease: [0.25, 1, 0.5, 1],
                }}
              >
                <AccountSelectItem
                  selected={selectedAccountIds.includes(account.id)}
                  onClick={() => onClickAccount(account.id)}
                  name={account.name}
                  type={account.type}
                  accountNumber={account.lastFour}
                  currency={account.currency}
                  balance={account.balance?.current}
                  showBalance={showBalances}
                  selectionMode={selectionMode}
                />
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </ConnectStep>
  );
};

export default AccountSelectStep;
